import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { Theme, useTheme } from '@mui/material/styles';
import { CircularProgress, Icon, Tooltip } from '@mui/material';
import styles from 'assets/jss/material-kit-react/views/profilePage.js';
import BubbleContainer from 'components/BubbleContainer/BubbleContainer';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Button from 'components/CustomButtons/Button.js';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardBody from 'components/Card/CardBody.js';
import NavPills from 'components/NavPills/NavPills.js';
import { Link } from 'react-router-dom';
import {
  AddShoppingCartOutlined,
  ArrowRightOutlined,
  BookmarkBorderOutlined,
  FestivalOutlined,
  GavelOutlined,
  HandshakeOutlined,
  LockClockOutlined,
  LoginOutlined,
  MenuBook,
  PersonAddOutlined,
  PersonOutline,
  QueryStatsOutlined,
  ShoppingCartCheckoutOutlined,
  WallpaperOutlined,
  WarningAmberOutlined,
  YoutubeSearchedForOutlined,
  ShoppingCartOutlined,
  HomeOutlined,
} from '@mui/icons-material';
import { secondaryColor } from '../../assets/jss/material-kit-react';
import { ItemCard, type ItemData } from '../../components/ItemCard';
import d2rsIconBlock from 'assets/img/d2rsiconblock.png';
// import d2rshopLogo from 'assets/img/d2rshopicon.png';
import d2ricon from 'assets/img/d2r-logo.png';
import axios from 'axios';
import config from '../../config/config.json';
import anyClass from '../../assets/img/pvp.png';
import barbarianClass from '../../assets/img/barb.png';
import sorceressClass from '../../assets/img/soso.png';
import amazonClass from '../../assets/img/ama.png';
import assassinClass from '../../assets/img/assa.png';
import necromancerClass from '../../assets/img/nec.png';
import paladinClass from '../../assets/img/pala.png';
import druidClass from '../../assets/img/dudu.png';
import toast from 'react-hot-toast';
import PaginationControls from '../../components/PaginationControls/PaginationControls';
import { useHeaderLinks } from 'components/Header/HeaderLinksContext';
import FeatureBox from '../../components/FeatureBox/FeatureBox';
import ToolsIcon from '../../components/ToolsIcon/ToolsIcon';

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '6px',
    color: theme.palette.text.primary,
  },
  discordButton: {
    color: '#7289da !important',
    borderColor: '#7289da !important',
    '&:hover': {
      borderColor: '#FFD700 !important',
    },
  },
  container: {
    ...styles.container,
    position: 'relative',
    zIndex: 3,
  },
  contentContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    zIndex: 1100,
    '& .MuiTabs-root': {
      zIndex: 1200,
    },
    '& .MuiTab-root': {
      zIndex: 1200,
    },
    '& .MuiTabs-root + div': {
      marginTop: 0,
    },
    '& .MuiGrid-root': {
      marginLeft: 0,
      width: '100%',
    },
  },
  cardHeader: {
    textAlign: 'center',
    '& h4': {
      marginTop: '0',
      marginBottom: '0',
      fontSize: '2rem',
    },
  },
  featureCard: {
    padding: '20px',
    borderRadius: '6px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    height: '100%',
    minHeight: '240px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    border: '1px solid rgba(241, 198, 43, 0.3)',
    marginBottom: '2rem',
  },
  mainCard: {
    padding: '20px',
    borderRadius: '6px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    marginBottom: '2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    border: '1px solid rgba(241, 198, 43, 0.3)',
  },
  featureIcon: {
    fontSize: '48px',
    marginBottom: '20px',
    color: theme.palette.primary.main,
  },
  section: {
    marginBottom: '2rem',
  },
  warningContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '10px',
    marginBottom: '20px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '4px',
    color: theme.palette.primary.contrastText,
    '& .MuiSvgIcon-root': {
      color: theme.palette.warning.main,
    },
  },
  navPillsContainer: {
    marginBottom: '2rem',
  },
  card: {
    marginTop: '30px',
  },
  runeButton: {
    marginBottom: '32px !important',
  },
}));

export default function LandingPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  useDocumentTitle('D2RS');
  const sessionToken = localStorage.getItem('session_token');
  const { isLoggedIn, userData } = useHeaderLinks();

  const handleTabChange = () => {};

  const mainContent = (
    <div className={classes.section}>
      <div className={classes.mainCard}>
        {isLoggedIn ? (
          <div style={{ marginBottom: '20px' }}>
            <h4>{t('landing.welcomeUser')}</h4>
            <h4>
              {userData?.profile_image ? (
                <img
                  src={userData.profile_image}
                  alt="profile"
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    verticalAlign: 'middle',
                    marginRight: '8px',
                  }}
                />
              ) : (
                <PersonOutline sx={{ fontSize: 24, verticalAlign: 'middle', marginRight: '8px' }} />
              )}{' '}
              {userData?.user?.name || ''}
            </h4>
          </div>
        ) : (
          <div style={{ marginBottom: '20px' }}>
            <h4>
              <PersonOutline sx={{ fontSize: 24, verticalAlign: 'middle', marginRight: '8px' }} />
              {t('landing.welcomeUser')} {t('landing.visitor')}
            </h4>
            <p style={{ marginTop: '10px', fontSize: '0.9rem', color: 'inherit' }}>
              {t('landing.developmentNotice')}
            </p>
            <Link to="/login">
              <Button color="outlined" size="sm">
                <LoginOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                {t('navigation.login')}
                <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
              </Button>
            </Link>
            <Link to="/register">
              <Button color="outlined" size="sm" style={{ marginLeft: '5px' }}>
                <PersonAddOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                {t('navigation.register')}
                <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
              </Button>
            </Link>
            <div style={{ marginTop: '10px' }}>
              <a
                href={`https://discord.com/api/oauth2/authorize?client_id=${config.discord_client_id}&redirect_uri=${encodeURIComponent(
                  `${window.location.origin}/auth/discord/callback`
                )}&response_type=code&scope=identify%20email`}
                style={{ textDecoration: 'none' }}
              >
                <Button color="outlined" size="sm" className={classes.discordButton}>
                  <Icon
                    className="fab fa-discord"
                    style={{ marginRight: '5px', fontSize: '16px', color: '#7289da' }}
                  />
                  {t('navigation.signInWithDiscord')}
                  <ArrowRightOutlined style={{ marginLeft: '5px', color: '#7289da' }} />
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const featuresContent = (
    <div className={classes.section}>
      <GridContainer spacing={4} sx={{ mb: 4 }}>
        <GridItem xs={12} sm={12} md={6}>
          <FeatureBox
            icon={<ShoppingCartOutlined />}
            title={t('landing.features.instantBuy.title')}
            description={t('landing.features.instantBuy.description')}
            buttons={
              <Link to="/shop">
                <Button color="outlined" size="sm">
                  <ShoppingCartCheckoutOutlined />
                  {t('navigation.instantShop')}
                  <ArrowRightOutlined />
                </Button>
              </Link>
            }
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <FeatureBox
            icon={<HomeOutlined />}
            title={t('landing.features.search.title')}
            description={t('landing.features.search.description')}
            buttons={[
              <div key="row1">
                <Link to="/tour-pages?t=tours">
                  <Button color="outlined" size="sm">
                    <FestivalOutlined />
                    {t('landing.tour')}
                    <ArrowRightOutlined />
                  </Button>
                </Link>
                <Link to="/guides">
                  <Button color="outlined" size="sm">
                    <MenuBook />
                    {t('landing.build')}
                    <ArrowRightOutlined />
                  </Button>
                </Link>
              </div>,
              <div key="row2">
                <Link to="/get-involved">
                  <Button color="outlined" size="sm">
                    <HandshakeOutlined />
                    {t('landing.work')}
                    <ArrowRightOutlined />
                  </Button>
                </Link>
              </div>,
            ]}
          />
        </GridItem>
        <GridItem xs={12}>
          <FeatureBox
            variant="large"
            icon={<ToolsIcon />}
            title={t('landing.features.uniqueTools.title')}
            description={t('landing.features.uniqueTools.description')}
            buttons={[
              <div key="row1">
                <Link to="/offers-page?t=howtouse">
                  <Button color="outlined" size="sm">
                    <GavelOutlined />
                    {t('navigation.offers')}
                    <ArrowRightOutlined />
                  </Button>
                </Link>
                <Link to="/saved-searches?t=howToUse">
                  <Button color="outlined" size="sm">
                    <YoutubeSearchedForOutlined />
                    {t('navigation.savedSearches')}
                    <ArrowRightOutlined />
                  </Button>
                </Link>
                <Link to="/tracker?t=howToUse">
                  <Button color="outlined" size="sm">
                    <BookmarkBorderOutlined />
                    {t('navigation.itemTracker')}
                    <ArrowRightOutlined />
                  </Button>
                </Link>
                <Link to="/event-mod">
                  <Button color="outlined" size="sm">
                    <FestivalOutlined />
                    {t('navigation.eventMod')}
                    <ArrowRightOutlined />
                  </Button>
                </Link>
              </div>,
            ]}
            lockedItemsTitle={t('landing.comingSoon')}
            lockedItems={
              <div className="buttonRow">
                <Button color="outlined" size="sm" disabled>
                  <WallpaperOutlined />
                  {t('navigation.imageGenerator')}
                  <LockClockOutlined />
                </Button>
                <Button color="outlined" size="sm" disabled>
                  <QueryStatsOutlined />
                  {t('navigation.priceIndex')}
                  <LockClockOutlined />
                </Button>
                <Button color="outlined" size="sm" disabled>
                  <Icon className="fab fa-discord" />
                  {t('navigation.discordCompanion')}
                  <LockClockOutlined />
                </Button>
              </div>
            }
          />
        </GridItem>
      </GridContainer>
    </div>
  );

  const [topRareItems, setTopRareItems] = useState<ItemData[]>([]);
  const [loadingRareItems, setLoadingRareItems] = useState(true);
  const [topRunes, setTopRunes] = useState<ItemData[]>([]);
  const [loadingRunes, setLoadingRunes] = useState(true);
  const [currentRareItemPage, setCurrentRareItemPage] = useState(1);

  const rareItemsPerPage = 4;

  useEffect(() => {
    const fetchTopRareItems = async () => {
      try {
        const response = await axios.post(
          config.backend_base_url + 'public/pss/search',
          {
            search_term: ',rare',
          },
          { headers: { 'session-token': sessionToken } }
        );
        if (response.data && response.data.length > 0) {
          setTopRareItems(response.data);
        }
      } catch (error) {
        toast.error(t('search.errors.fetchFailed', 'Failed to fetch rare items'));
      } finally {
        setLoadingRareItems(false);
      }
    };

    const fetchTopRunes = async () => {
      try {
        // Fetch Jah runes
        const jahResponse = await axios.post(
          config.backend_base_url + 'ml/pickit/dynamic/psearch',
          {
            search_term: '.jah rune',
          },
          { headers: { 'session-token': sessionToken } }
        );

        // Fetch Ber runes
        const berResponse = await axios.post(
          config.backend_base_url + 'ml/pickit/dynamic/psearch',
          {
            search_term: '.ber rune',
          },
          { headers: { 'session-token': sessionToken } }
        );

        // Fetch Lo runes
        const loResponse = await axios.post(
          config.backend_base_url + 'ml/pickit/dynamic/psearch',
          {
            search_term: '.lo rune',
          },
          { headers: { 'session-token': sessionToken } }
        );

        // Fetch Sur runes
        const surResponse = await axios.post(
          config.backend_base_url + 'ml/pickit/dynamic/psearch',
          {
            search_term: '.sur rune',
          },
          { headers: { 'session-token': sessionToken } }
        );

        // Map the data for each rune type, ensuring we get up to 4 of each
        const mappedJahRunes = Array.isArray(jahResponse.data)
          ? jahResponse.data.slice(0, 4).map((rune) => ({
              id: rune.id,
              name: rune.title || rune.name,
              title: rune.title || rune.name,
              context: rune.ladder
                ? rune.hardcore
                  ? 'hcl'
                  : 'scl'
                : rune.hardcore
                  ? 'hcnl'
                  : 'scnl',
              product_image_path: rune.product_image_path || rune.image_path,
              skin_image_path: rune.skin_image_path,
              d2rshopproductlink: rune.shop_product_link || '',
              fiat_price:
                rune.fg_price !== '???'
                  ? ((parseFloat(rune.fg_price) / 166) * (rune.shop_price_factor || 1)).toFixed(2)
                  : '???',
              ladder: rune.ladder,
              hardcore: rune.hardcore,
              original_rarity: 'RUNE',
              stats_description: rune.stats || '',
              is_product: true,
            }))
          : [];

        const mappedBerRunes = Array.isArray(berResponse.data)
          ? berResponse.data.slice(0, 4).map((rune) => ({
              id: rune.id,
              name: rune.title || rune.name,
              title: rune.title || rune.name,
              context: rune.ladder
                ? rune.hardcore
                  ? 'hcl'
                  : 'scl'
                : rune.hardcore
                  ? 'hcnl'
                  : 'scnl',
              product_image_path: rune.product_image_path || rune.image_path,
              skin_image_path: rune.skin_image_path,
              d2rshopproductlink: rune.shop_product_link || '',
              fiat_price:
                rune.fg_price !== '???'
                  ? ((parseFloat(rune.fg_price) / 166) * (rune.shop_price_factor || 1)).toFixed(2)
                  : '???',
              ladder: rune.ladder,
              hardcore: rune.hardcore,
              original_rarity: 'RUNE',
              stats_description: rune.stats || '',
              is_product: true,
            }))
          : [];

        const mappedSurRunes = Array.isArray(surResponse.data)
          ? surResponse.data.slice(0, 4).map((rune) => ({
              id: rune.id,
              name: rune.title || rune.name,
              title: rune.title || rune.name,
              context: rune.ladder
                ? rune.hardcore
                  ? 'hcl'
                  : 'scl'
                : rune.hardcore
                  ? 'hcnl'
                  : 'scnl',
              product_image_path: rune.product_image_path || rune.image_path,
              skin_image_path: rune.skin_image_path,
              d2rshopproductlink: rune.shop_product_link || '',
              fiat_price:
                rune.fg_price !== '???'
                  ? ((parseFloat(rune.fg_price) / 166) * (rune.shop_price_factor || 1)).toFixed(2)
                  : '???',
              ladder: rune.ladder,
              hardcore: rune.hardcore,
              original_rarity: 'RUNE',
              stats_description: rune.stats || '',
              is_product: true,
            }))
          : [];

        const mappedLoRunes = Array.isArray(loResponse.data)
          ? loResponse.data.slice(0, 4).map((rune) => ({
              id: rune.id,
              name: rune.title || rune.name,
              title: rune.title || rune.name,
              context: rune.ladder
                ? rune.hardcore
                  ? 'hcl'
                  : 'scl'
                : rune.hardcore
                  ? 'hcnl'
                  : 'scnl',
              product_image_path: rune.product_image_path || rune.image_path,
              skin_image_path: rune.skin_image_path,
              d2rshopproductlink: rune.shop_product_link || '',
              fiat_price:
                rune.fg_price !== '???'
                  ? ((parseFloat(rune.fg_price) / 166) * (rune.shop_price_factor || 1)).toFixed(2)
                  : '???',
              ladder: rune.ladder,
              hardcore: rune.hardcore,
              original_rarity: 'RUNE',
              stats_description: rune.stats || '',
              is_product: true,
            }))
          : [];

        // Combine all runes in the specified order: Jah, Ber, Sur, Lo
        const combinedRunes = [
          ...mappedJahRunes,
          ...mappedBerRunes,
          ...mappedSurRunes,
          ...mappedLoRunes,
        ];

        // Sort runes by context: SCL, SCNL, HCL, HCNL
        const contextOrder = {
          scl: 0,
          scnl: 1,
          hcl: 2,
          hcnl: 3,
        };

        const sortedRunes = combinedRunes.sort((a, b) => {
          const contextA = a.context.toLowerCase();
          const contextB = b.context.toLowerCase();
          return contextOrder[contextA] - contextOrder[contextB];
        });

        if (sortedRunes.length > 0) {
          setTopRunes(sortedRunes);
        }
      } catch (error) {
        toast.error(t('search.errors.fetchFailed', 'Failed to fetch runes'));
      } finally {
        setLoadingRunes(false);
      }
    };

    fetchTopRareItems();
    fetchTopRunes();
  }, [sessionToken, t]);

  const topRareItemsContent = (
    <Card className={classes.card}>
      <CardHeader color="primary" className={classes.cardHeader}>
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}
        >
          <h4>{t('landing.topRareItems')}</h4>
        </div>
      </CardHeader>
      <CardBody>
        <Link to="/shop?term=,rare">
          <Button color="outlined" size="sm">
            <ShoppingCartCheckoutOutlined style={{ marginRight: '5px', color: 'inherit' }} />
            {t('common.viewAll')}
            <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
          </Button>
        </Link>
        <PaginationControls
          data={topRareItems}
          currentPage={currentRareItemPage}
          setCurrentPage={setCurrentRareItemPage}
          itemsPerPage={4}
        />
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '24px',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: '24px',
            width: '100%',
          }}
        >
          {loadingRareItems ? (
            <div style={{ textAlign: 'center', width: '100%' }}>
              <CircularProgress />
            </div>
          ) : (
            <GridContainer spacing={3}>
              {topRareItems
                .slice(
                  (currentRareItemPage - 1) * rareItemsPerPage,
                  currentRareItemPage * rareItemsPerPage
                )
                .map((item, key) => (
                  <GridItem xs={12} sm={6} md={3} key={key}>
                    <ItemCard
                      item={item}
                      sessionToken={sessionToken}
                      itemsPerPage={rareItemsPerPage}
                    />
                  </GridItem>
                ))}
            </GridContainer>
          )}
        </div>
        <PaginationControls
          data={topRareItems}
          currentPage={currentRareItemPage}
          setCurrentPage={setCurrentRareItemPage}
          itemsPerPage={4}
        />
      </CardBody>
    </Card>
  );

  const topRunesContent = (
    <Card className={classes.card}>
      <CardHeader color="primary" className={classes.cardHeader}>
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}
        >
          <h4>{t('landing.topRunes')}</h4>
        </div>
      </CardHeader>
      <CardBody>
        <Link to="/shop?term=,rune">
          <Button color="outlined" size="sm" className={classes.runeButton}>
            <AddShoppingCartOutlined style={{ marginRight: '5px', color: 'inherit' }} />
            {t('common.viewAll')}
            <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
          </Button>
        </Link>
        {loadingRunes ? (
          <div style={{ textAlign: 'center', width: '100%' }}>
            <CircularProgress />
          </div>
        ) : (
          <GridContainer
            spacing={3}
            style={{ marginTop: '0 !important', background: 'green !important' }}
          >
            {topRunes.map((item, key) => (
              <GridItem xs={12} sm={6} md={4} xl={3} key={key}>
                <ItemCard item={item} sessionToken={sessionToken} variant="rune" />
              </GridItem>
            ))}
          </GridContainer>
        )}
      </CardBody>
    </Card>
  );

  return (
    <div>
      <div className={classes.mainContainer}>
        <div className={classes.container}>
          <div className={classes.contentContainer}>
            <Card>
              <CardHeader color="primary" className={classes.cardHeader}>
                <h4>{t('landing.title')}</h4>
              </CardHeader>
              <div className={classes.warningContainer}>
                <WarningAmberOutlined />
                <span>{t('landing.disclaimer')}</span>
              </div>
              <CardBody>
                <div className={classes.navPillsContainer}>
                  <NavPills
                    color="primary"
                    queryKey="t"
                    onChange={handleTabChange}
                    alignCenter
                    tabs={[
                      {
                        tabButton: (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <img src={d2rsIconBlock} alt="D2RS" style={{ height: '48px' }} />
                            <span
                              style={{ fontWeight: 700, fontSize: '24px', color: secondaryColor }}
                            >
                              ×
                            </span>
                            <img
                              src={d2ricon}
                              alt="Diablo II: Resurrected"
                              style={{ height: '48px' }}
                            />
                          </div>
                        ),
                        tabContent: (
                          <span>
                            {mainContent}
                            {featuresContent}
                          </span>
                        ),
                        tabName: 'shop',
                      },
                    ]}
                  />
                </div>
              </CardBody>
            </Card>
            {topRareItemsContent}
            {topRunesContent}
            <Card className={classes.card}>
              <CardHeader color="primary" className={classes.cardHeader}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                  }}
                >
                  <h4>{t('landing.fullChars')}</h4>
                </div>
              </CardHeader>
              <CardBody>
                <Link to="/tour-pages?t=tours">
                  <Button color="outlined" size="sm">
                    <FestivalOutlined style={{ marginRight: '5px', color: 'inherit' }} />
                    {t('landing.tour')}
                    <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
                  </Button>
                </Link>
                <Link to="/guides">
                  <Button color="outlined" size="sm">
                    <MenuBook style={{ marginRight: '5px', color: 'inherit' }} />
                    {t('landing.build')}
                    <ArrowRightOutlined style={{ marginLeft: '5px', color: 'inherit' }} />
                  </Button>
                </Link>
                <NavPills
                  color="primary"
                  queryKey="class"
                  alignCenter
                  tabs={[
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={anyClass}
                            alt="General"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.general')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <PaginationControls
                                      data={topRareItems}
                                      currentPage={currentRareItemPage}
                                      setCurrentPage={setCurrentRareItemPage}
                                      itemsPerPage={8}
                                    />
                                    <GridContainer spacing={3}>
                                      {loadingRareItems ? (
                                        <GridItem xs={12}>
                                          <div style={{ textAlign: 'center', width: '100%' }}>
                                            <CircularProgress />
                                          </div>
                                        </GridItem>
                                      ) : (
                                        topRareItems
                                          .slice(
                                            (currentRareItemPage - 1) * 8,
                                            currentRareItemPage * 8
                                          )
                                          .map((item, key) => (
                                            <GridItem xs={12} sm={6} md={3} key={key}>
                                              <ItemCard
                                                item={item}
                                                sessionToken={sessionToken}
                                                itemsPerPage={8}
                                              />
                                            </GridItem>
                                          ))
                                      )}
                                    </GridContainer>
                                    <PaginationControls
                                      data={topRareItems}
                                      currentPage={currentRareItemPage}
                                      setCurrentPage={setCurrentRareItemPage}
                                      itemsPerPage={8}
                                    />
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.itemPacks'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'item-packs',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'general',
                    },
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={barbarianClass}
                            alt="Barbarian"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.barbarian')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvpChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvp-chars',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvmChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvm-chars',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'barbarian',
                    },
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={sorceressClass}
                            alt="Sorceress"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.sorceress')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvpChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvp-chars',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvmChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvm-chars',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'sorceress',
                    },
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={amazonClass}
                            alt="Amazon"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.amazon')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvpChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvp-chars',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvmChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvm-chars',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'amazon',
                    },
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={assassinClass}
                            alt="Assassin"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.assassin')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvpChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvp-chars',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvmChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvm-chars',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'assassin',
                    },
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={necromancerClass}
                            alt="Necromancer"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.necromancer')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvpChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvp-chars',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvmChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvm-chars',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'necromancer',
                    },
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={paladinClass}
                            alt="Paladin"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.paladin')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvpChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvp-chars',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvmChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvm-chars',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'paladin',
                    },
                    {
                      tabButton: (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <img
                            src={druidClass}
                            alt="Druid"
                            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                          />
                          {t('guides.class.druid')}
                        </div>
                      ),
                      tabContent: (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <NavPills
                            color="primary"
                            queryKey="category"
                            alignCenter
                            tabs={[
                              {
                                tabButton: t('landing.fullChars.dedicatedItems'),
                                tabContent: (
                                  <div
                                    style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      border: '1px solid rgba(241, 198, 43, 0.3)',
                                      borderRadius: '6px',
                                    }}
                                  >
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'dedicated-items',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvpChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvp-chars',
                              },
                              {
                                tabButton: t('landing.fullChars.fullPvmChars'),
                                tabContent: (
                                  <div style={{ textAlign: 'center', padding: '20px' }}>
                                    <Tooltip title={t('landing.comingSoon')} arrow placement="top">
                                      <LockClockOutlined
                                        style={{
                                          fontSize: '48px',
                                          marginBottom: '10px',
                                          opacity: 0.7,
                                          color: theme.palette.text.secondary,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                ),
                                tabName: 'full-pvm-chars',
                              },
                            ]}
                          />
                        </div>
                      ),
                      tabName: 'druid',
                    },
                  ]}
                />
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
      <BubbleContainer pageKey="landing" helpText={t('help.landing')} />
    </div>
  );
}
